import React, { useEffect, useMemo, useState } from 'react';
import { Activity, Car, Clock, Download, MapPin, Navigation, Route, Search } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import { positionService, vehicleService } from '../services/api';
import { useVehicleStore } from '../store';
import { toast } from 'react-toastify';
import { format, subDays } from 'date-fns';
import { exportCsv } from '../utils/csv';

const toInputDateTime = (date) => {
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const History = () => {
  const { vehicles, setVehicles, setLoading, loading } = useVehicleStore();
  const [vehicleId, setVehicleId] = useState('');
  const [startDate, setStartDate] = useState(toInputDateTime(subDays(new Date(), 1)));
  const [endDate, setEndDate] = useState(toInputDateTime(new Date()));
  const [routeData, setRouteData] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadVehicles();
  }, []);

  useEffect(() => {
    if (!vehicleId && vehicles.length > 0) {
      setVehicleId(vehicles[0].id);
    }
  }, [vehicleId, vehicles]);

  const selectedVehicle = vehicles.find((vehicle) => vehicle.id === vehicleId);
  const positions = routeData?.positions || [];
  const stats = routeData?.stats || { totalPoints: 0, maxSpeed: 0, avgSpeed: 0 };

  const filteredPositions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return positions;

    return positions.filter((position) => (
      position.address?.toLowerCase().includes(term) ||
      String(position.speed || '').includes(term) ||
      String(position.latitude || '').includes(term) ||
      String(position.longitude || '').includes(term)
    ));
  }, [positions, search]);

  const loadVehicles = async () => {
    setLoading(true);

    try {
      const response = await vehicleService.getAll();
      setVehicles(response.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao carregar veículos');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchRoute = async (event) => {
    event.preventDefault();

    if (!vehicleId) {
      toast.error('Selecione um veículo');
      return;
    }

    if (!startDate || !endDate) {
      toast.error('Informe o período da consulta');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      toast.error('A data inicial precisa ser anterior à data final');
      return;
    }

    setRouteLoading(true);

    try {
      const response = await positionService.getRoute(vehicleId, {
        startDate: new Date(startDate).toISOString(),
        endDate: new Date(endDate).toISOString(),
      });
      setRouteData(response.data.data);
      toast.success('Histórico carregado');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao carregar histórico');
    } finally {
      setRouteLoading(false);
    }
  };

  const handleExport = () => {
    exportCsv('rcrastro-historico.csv', [
      { key: 'device_time', label: 'Horario' },
      { key: 'vehicle', label: 'Veiculo' },
      { key: 'plate', label: 'Placa' },
      { key: 'address', label: 'Endereco' },
      { key: 'speed', label: 'Velocidade' },
      { key: 'ignition', label: 'Ignicao' },
      { key: 'latitude', label: 'Latitude' },
      { key: 'longitude', label: 'Longitude' },
    ], filteredPositions.map((position) => ({
      device_time: position.device_time ? format(new Date(position.device_time), 'dd/MM/yyyy HH:mm') : '',
      vehicle: selectedVehicle?.name || '',
      plate: selectedVehicle?.plate || '',
      address: position.address || '',
      speed: Number(position.speed || 0).toFixed(0),
      ignition: position.ignition ? 'Ligada' : 'Desligada',
      latitude: position.latitude,
      longitude: position.longitude,
    })));
    toast.success('CSV de histórico gerado');
  };

  return (
    <div className="flex h-screen bg-dark-950">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Histórico</h1>
              <p className="text-dark-400">Consulte posições registradas e rotas por período.</p>
            </div>

            <button
              type="button"
              onClick={handleExport}
              disabled={filteredPositions.length === 0}
              className="btn-secondary flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              Exportar CSV
            </button>
          </div>

          <form onSubmit={handleSearchRoute} className="card mb-6">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-dark-300">
                  Veículo
                </label>
                <select
                  value={vehicleId}
                  onChange={(event) => setVehicleId(event.target.value)}
                  className="input-field"
                  disabled={loading}
                >
                  {vehicles.length === 0 ? (
                    <option value="">Nenhum veículo</option>
                  ) : (
                    vehicles.map((vehicle) => (
                      <option key={vehicle.id} value={vehicle.id}>
                        {vehicle.name} - {vehicle.plate}
                      </option>
                    ))
                  )}
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-dark-300">
                  Data inicial
                </label>
                <input
                  type="datetime-local"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                  className="input-field"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-dark-300">
                  Data final
                </label>
                <input
                  type="datetime-local"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                  className="input-field"
                />
              </div>

              <div className="flex items-end">
                <button
                  type="submit"
                  disabled={routeLoading || !vehicleId}
                  className="btn-primary flex w-full items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Route className="h-4 w-4" />
                  {routeLoading ? 'Consultando...' : 'Consultar'}
                </button>
              </div>
            </div>
          </form>

          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="card">
              <div className="mb-2 flex items-center gap-2 text-dark-400">
                <Car className="h-4 w-4" />
                <span className="text-sm">Veículo</span>
              </div>
              <p className="text-lg font-semibold text-white">{selectedVehicle?.name || 'Selecione'}</p>
              <p className="text-sm text-dark-400">{selectedVehicle?.plate}</p>
            </div>

            <div className="card">
              <div className="mb-2 flex items-center gap-2 text-dark-400">
                <MapPin className="h-4 w-4" />
                <span className="text-sm">Pontos</span>
              </div>
              <p className="text-3xl font-bold text-white">{stats.totalPoints || positions.length}</p>
            </div>

            <div className="card">
              <div className="mb-2 flex items-center gap-2 text-dark-400">
                <Activity className="h-4 w-4" />
                <span className="text-sm">Velocidade máxima</span>
              </div>
              <p className="text-3xl font-bold text-warning-400">{Number(stats.maxSpeed || 0).toFixed(0)} km/h</p>
            </div>

            <div className="card">
              <div className="mb-2 flex items-center gap-2 text-dark-400">
                <Navigation className="h-4 w-4" />
                <span className="text-sm">Velocidade média</span>
              </div>
              <p className="text-3xl font-bold text-primary-400">{Number(stats.avgSpeed || 0).toFixed(0)} km/h</p>
            </div>
          </div>

          <div className="card mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-dark-400" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="input-field pl-11"
                placeholder="Buscar por endereço, coordenada ou velocidade"
              />
            </div>
          </div>

          <div className="card p-0">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-left">
                <thead className="border-b border-dark-700 text-sm text-dark-400">
                  <tr>
                    <th className="px-5 py-4 font-medium">Horário</th>
                    <th className="px-5 py-4 font-medium">Localização</th>
                    <th className="px-5 py-4 font-medium">Velocidade</th>
                    <th className="px-5 py-4 font-medium">Ignição</th>
                    <th className="px-5 py-4 font-medium">Coordenadas</th>
                  </tr>
                </thead>
                <tbody>
                  {routeLoading ? (
                    <tr>
                      <td colSpan="5" className="px-5 py-10 text-center text-dark-400">
                        Carregando histórico...
                      </td>
                    </tr>
                  ) : filteredPositions.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-5 py-10 text-center text-dark-400">
                        <Clock className="mx-auto mb-2 h-10 w-10 opacity-50" />
                        Nenhum ponto encontrado para o período
                      </td>
                    </tr>
                  ) : (
                    filteredPositions.map((position) => (
                      <tr key={position.id} className="border-b border-dark-800 text-sm last:border-0">
                        <td className="px-5 py-4 text-white">
                          {position.device_time ? format(new Date(position.device_time), 'dd/MM/yyyy HH:mm') : '-'}
                        </td>
                        <td className="px-5 py-4 text-dark-300">
                          {position.address || 'Endereço não informado'}
                        </td>
                        <td className="px-5 py-4 text-dark-300">
                          {Number(position.speed || 0).toFixed(0)} km/h
                        </td>
                        <td className="px-5 py-4">
                          <span className={`badge ${position.ignition ? 'badge-success' : 'badge-warning'}`}>
                            {position.ignition ? 'Ligada' : 'Desligada'}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-dark-400">
                          {Number(position.latitude).toFixed(6)}, {Number(position.longitude).toFixed(6)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default History;
